import { useTheme } from "next-themes";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { useSoundOptional } from "@/contexts/SoundContext";
import { useEffect, useRef } from "react";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const soundContext = useSoundOptional();
  const lastToastRef = useRef<string | null>(null);

  // Hook into toast events to play sounds
  useEffect(() => {
    if (!soundContext) return;

    const originalSuccess = sonnerToast.success;
    const originalError = sonnerToast.error;
    const originalInfo = sonnerToast.info;
    const originalWarning = sonnerToast.warning;

    // Override toast methods to add sounds
    (sonnerToast as any).success = (...args: Parameters<typeof originalSuccess>) => {
      soundContext.playSound('success');
      return originalSuccess(...args);
    };

    (sonnerToast as any).error = (...args: Parameters<typeof originalError>) => {
      soundContext.playSound('error');
      return originalError(...args);
    };

    (sonnerToast as any).info = (...args: Parameters<typeof originalInfo>) => {
      soundContext.playSound('notification');
      return originalInfo(...args);
    };

    (sonnerToast as any).warning = (...args: Parameters<typeof originalWarning>) => {
      soundContext.playSound('notification');
      return originalWarning(...args);
    };

    return () => {
      // Restore original methods
      (sonnerToast as any).success = originalSuccess;
      (sonnerToast as any).error = originalError;
      (sonnerToast as any).info = originalInfo;
      (sonnerToast as any).warning = originalWarning;
    };
  }, [soundContext]);

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

// Re-export toast for convenience
const toast = sonnerToast;

export { Toaster, toast };
